import { apiFetch } from './client';
import type { BoardCard, BoardCardUpdate, BoardUpdateStatus, BoardColumn, HousekeepingItem, NoWorkDay, NoWorkDayBody, TeamBoardResponse, TeamBoardActivityResponse, TeamBoardUsageRow } from '$lib/types';

export interface BoardCardBody {
  title?: string;
  description?: string;
  col?: BoardColumn;
  position?: number;
  due_date?: string | null;
}

export interface BoardUpdateBody { text: string; status: BoardUpdateStatus; waiting_on: string; next_followup: string | null }

export interface TeamBoardCardBody extends BoardCardBody {
  user_id: number;
  col: BoardColumn;
}
export interface BoardHelper { user_id: number; title: string }
export interface BoardBlockBody { reason: string; helpers: BoardHelper[]; external?: { name: string }; update?: Omit<BoardUpdateBody, 'status'> }

// boardApi is the personal-kanban surface (GET/POST/PATCH/DELETE
// /api/board*) — cards are owned by exactly one user and may be self-created,
// assigned from the team board, or created as helper tasks for a blocked card.
export const boardApi = {
  recordView: (id: number, source: 'board_open' | 'card_open') =>
    apiFetch<unknown>(`/board/cards/${id}/views`, { method: 'POST', credentials: 'same-origin', body: JSON.stringify({ source }) }),
  updates: (id: number, team = false) => apiFetch<BoardCardUpdate[]>(`/board/${team ? 'team/' : ''}cards/${id}/updates`),
  addUpdate: (id: number, body: BoardUpdateBody, team = false) =>
    apiFetch<BoardCardUpdate>(`/board/${team ? 'team/' : ''}cards/${id}/updates`, { method: 'POST', body: JSON.stringify(body) }),
  returnToRequester: (id: number, text: string, team = false) =>
    apiFetch<BoardCard>(`/board/${team ? 'team/' : ''}cards/${id}/return`, { method: 'POST', body: JSON.stringify({ text }) }),
  list: () => apiFetch<BoardCard[]>('/board'),
  team: (userId?: number) =>
    apiFetch<TeamBoardResponse>(userId ? `/board/team?user_id=${userId}` : '/board/team'),
  teamUpdate: (id: number, body: BoardCardBody) =>
    apiFetch<BoardCard>(`/board/team/cards/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  teamCreate: (body: TeamBoardCardBody) =>
    apiFetch<BoardCard>('/board/team/cards', { method: 'POST', body: JSON.stringify(body) }),
  teamAssign: (body: { user_ids:number[]; title:string; description?:string; col?:BoardColumn }) =>
    apiFetch<BoardCard[]>('/board/team/cards/assign', { method: 'POST', body: JSON.stringify(body) }),
  teamRemove: (id: number) => apiFetch<null>(`/board/team/cards/${id}`, { method: 'DELETE' }),
   teamActivity: (userIds: number[] = []) => apiFetch<TeamBoardActivityResponse>(`/board/team/activity${userIds.length ? `?user_ids=${userIds.join(',')}` : ''}`),
   teamUsage: () => apiFetch<TeamBoardUsageRow[]>('/board/team/usage'),
   recordOpen: () => apiFetch<{ ok: boolean }>('/board/opened', { method: 'POST' }),
  create: (body: BoardCardBody) => apiFetch<BoardCard>('/board/cards', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: BoardCardBody) =>
    apiFetch<BoardCard>(`/board/cards/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  mentions: (q: string) => apiFetch<{id:number; name:string}[]>(`/board/mentions?q=${encodeURIComponent(q)}`),
  block: (id:number, body:BoardBlockBody) => apiFetch<BoardCard>(`/board/cards/${id}/block`, { method:'POST', body:JSON.stringify(body) }),
  teamBlock: (id:number, body:BoardBlockBody) => apiFetch<BoardCard>(`/board/team/cards/${id}/block`, { method:'POST', body:JSON.stringify(body) }),
  remove: (id: number) => apiFetch<null>(`/board/cards/${id}`, { method: 'DELETE' })
};

// housekeepingApi is the employee-facing read (GET /api/housekeeping, any
// authenticated caller, active items only) — used to populate the no-work
// dialog's "housekeeping" option. Admin CRUD lives in $lib/api/admin.
export const housekeepingApi = {
  list: () => apiFetch<HousekeepingItem[]>('/housekeeping')
};

export const noWorkApi = {
  list: () => apiFetch<NoWorkDay[]>('/board/no_work'),
  create: (body: NoWorkDayBody) => apiFetch<NoWorkDay>('/board/no_work', { method: 'POST', body: JSON.stringify(body) })
};
